import React, { useState, useEffect, useContext } from "react";
import {
  AppBar,
  Toolbar,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Avatar,
  Divider,
  Button,
  IconButton,
  Typography,
  Container,
  Box,
  CssBaseline,
  Popover,
  CircularProgress,
} from "@material-ui/core";
import { Menu as MenuIcon, AccountCircle, Home, AddBox, Dashboard, ExitToApp as ExitToAppIcon } from "@material-ui/icons";
import { Link, useHistory, useLocation } from "react-router-dom";
import { ReactComponent as ShoppingBagIcon } from "../assets/icons/shoppingBag.svg";
import { Auth } from 'aws-amplify';
import { AuthContext } from '../contexts/AuthContext';
import { PUBLIC_PATHS } from '../contexts/AuthContext';

const Default = ({ children, showFooter = true, noInnerBox = false }) => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(true);
  const [currentUserInfo, setCurrentUserInfo] = useState({});
  const [hoverIndex, setHoverIndex] = useState(null);
  const [headerColor, setHeaderColor] = useState("red");
  const [anchorEl, setAnchorEl] = useState(null);
  const [isHeaderVisible, setIsHeaderVisible] = useState(true);
  const [isSigningOut, setIsSigningOut] = useState(false);
  const history = useHistory();
  const location = useLocation();
  const { isAuthenticated, signOut } = useContext(AuthContext);

  const handleAvatarClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  useEffect(() => {
    const savedColor = localStorage.getItem("headerColor");
    if (savedColor) {
      setHeaderColor(savedColor);
    }
  }, []);

  const handleColorChange = (event) => {
    const newColor = event.target.value;
    setHeaderColor(newColor);
    localStorage.setItem("headerColor", newColor);
  };

  const containerStyle = {
    maxWidth: "1500px",
    backgroundColor: "white",
    borderRadius: "10px",
    boxShadow: "2px 2px 8px #cfcfcf, -2px -2px 8px #cfcfcf",
    padding: "50px",
    marginTop: "16px",
  };

  const menuItems = [
    { 
      icon: <Home />, 
      title: "ホーム", 
      to: "/", 
      requireAuth: true 
    },
    { 
      icon: <AccountCircle />, 
      title: "プロフィール", 
      to: "/profile", 
      requireAuth: true 
    },
    { 
      icon: <AddBox />, 
      title: "商品を作成", 
      to: "/products/new", 
      requireAuth: true 
    },
    { 
      icon: <Dashboard />, 
      title: "ダッシュボード", 
      to: "/dashboard", 
      requireAuth: true 
    },
    {
      icon: <ShoppingBagIcon style={{ width: 24, height: 24 }} />,
      title: "商品の再閲覧",
      to: "/customer",
      requireAuth: false  // 認証不要に設定
    },
  ];

  // メニューフィルタリングのログ
  const filteredMenuItems = menuItems.filter(item => {
    const shouldShow = !item.requireAuth || (item.requireAuth && isAuthenticated);
    return shouldShow;
  });

  // 認証状態の変化を監視し、isLoggedInを更新
  useEffect(() => {
    const checkAuthStatus = async () => {
      setIsLoggedIn(isAuthenticated);
      
      // 認証済みの場合のみユーザー情報を取得
      if (isAuthenticated) {
        try {
          // AWS Cognitoから現在のユーザー情報を取得
          const currentUser = await Auth.currentAuthenticatedUser();
          const userInfo = {
            attributes: {
              email: currentUser.attributes.email
            }
          };
          setCurrentUserInfo(userInfo);
          setIsHeaderVisible(true); // ヘッダーを確実に表示
        } catch (error) {
          console.error('ユーザー情報取得エラー:', error);
          setCurrentUserInfo({});
        }
      } else {
        setCurrentUserInfo({});
      }
    };
    
    checkAuthStatus();
  }, [isAuthenticated]);

  // サインアウト処理のログ
  const handleSignOut = async () => {
    try {
      setIsSigningOut(true);  // ローディング開始
      
      // 2秒間のディレイを追加
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      await Auth.signOut();
      if (signOut) {
        signOut();  // AuthContextのsignOutメソッドを呼び出し
      }
      setIsLoggedIn(false);
      setCurrentUserInfo({});
      setIsHeaderVisible(false);
      handlePopoverClose();  // ポップオーバーを閉じる
      history.push("/signin");
    } catch (error) {
      console.error("サインアウトエラー:", error);
    } finally {
      setIsSigningOut(false);  // ローディング終了
    }
  };

  return (
    <Box display="flex" flexDirection="column" minHeight="100vh">
    <CssBaseline />
      <AppBar position="fixed" style={{ backgroundColor: headerColor }}>
        <Toolbar>
          {/* 左側 */}
          <IconButton edge="start" color="inherit" onClick={() => setDrawerOpen(!drawerOpen)}>
            <MenuIcon />
          </IconButton>
          <Typography variant="h5" style={{ flexGrow: 1 }}>
            <Link to="/" style={{ textDecoration: "none", color: "inherit" }}>
              PittaPut
            </Link>
          </Typography>
          {/* 右寄りメニュー */}
          {isHeaderVisible && (
            <Typography
              variant="h6"
              style={{
                display: "flex",
                justifyContent: "flex-end", // リンクを右寄せ
                flexGrow: 1,
                gap: "16px", // リンク間のスペース
                fontSize: "15px", //フォントサイズを小さく
              }}
            >
              {filteredMenuItems.map((link, index) => (
                <Link
                  key={link.to}
                  to={link.to}
                  style={{
                    textDecoration: "none",
                    color: hoverIndex === index ? "white" : "white",
                    padding: "8px 16px",
                    borderRadius: "4px",
                    transition: "color 0.3s, background-color 0.3s",
                    backgroundColor: hoverIndex === index ? "darkred" : "transparent",
                  }}
                  onMouseEnter={() => setHoverIndex(index)}
                  onMouseLeave={() => setHoverIndex(null)}
                >
                  {link.title}
                </Link>
              ))}
            </Typography>
          )}

          {/* ヘッダーのユーザーアイコン - 認証済みの場合のみ表示 */}
          {isAuthenticated ? (
            <Box display="flex" alignItems="center">
              <Avatar
                style={{ marginLeft: "16px", backgroundColor: "indigo", cursor: "pointer" }}
                onClick={handleAvatarClick}
              >
                <AccountCircle />
              </Avatar>
              <Popover
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                onClose={handlePopoverClose}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "center",
                }}
                transformOrigin={{
                  vertical: "top",
                  horizontal: "center",
                }}
              >
                <Box display="flex" flexDirection="column" alignItems="center" gap="8px" p={2}>
                  <Typography variant="body1" style={{ marginBottom: "8px" }}>
                    {currentUserInfo.attributes?.email || "ユーザー"}
                  </Typography>
                  <Button
                    variant="outlined"
                    color="primary"
                    component={Link}
                    to="/profile"
                    style={{ height: "40px", marginTop: "8px", width: "150px" }}
                  >
                    プロフィール
                  </Button>
                  <Button
                    variant="outlined"
                    color="secondary"
                    onClick={handleSignOut}
                    disabled={isSigningOut}
                    style={{ height: "40px", marginTop: "8px", width: "150px" }}
                  >
                    {isSigningOut ? (
                      <CircularProgress size={24} color="secondary" />
                    ) : (
                      "サインアウト"
                    )}
                  </Button>
                  <Box mt={1} p={1}>
                    <label>
                      テーマカラー：
                      <input
                        type="color"
                        value={headerColor}
                        onChange={handleColorChange}
                        style={{ marginLeft: "5px" }}
                      />
                    </label>
                  </Box>
                </Box>
              </Popover>
            </Box>
          ) : (
            // 未認証の場合はサインインボタンのみ表示
            <Button color="inherit" component={Link} to="/signin">
              サインイン
            </Button>
          )}
        </Toolbar>
      </AppBar>

      <Drawer anchor="left" open={drawerOpen} onClose={() => setDrawerOpen(false)}>
        <Box width={250}>
          <List>
            {isAuthenticated && (
              <Box display="flex" alignItems="center" padding="16px">
                <Avatar style={{ backgroundColor: "indigo" }}>
                  <AccountCircle />
                </Avatar>
                <Typography variant="body1" style={{ marginLeft: "8px" }}>
                  {currentUserInfo.attributes?.email}
                </Typography>
              </Box>
            )}
            <Divider />
            {filteredMenuItems.map((item, index) => (
              <ListItem button key={index} component={Link} to={item.to}>
                <ListItemIcon>{item.icon}</ListItemIcon>
                <ListItemText primary={item.title} />
              </ListItem>
            ))}
            {!isAuthenticated && (
              <ListItem button component={Link} to="/signin">
                <ListItemIcon><AccountCircle /></ListItemIcon>
                <ListItemText primary="サインイン" />
              </ListItem>
            )}
            {isAuthenticated && (
              <ListItem 
                button 
                onClick={handleSignOut}
                disabled={isSigningOut}
              >
                <ListItemIcon>
                  {isSigningOut ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    <ExitToAppIcon />
                  )}
                </ListItemIcon>
                <ListItemText primary={isSigningOut ? "サインアウト中..." : "サインアウト"} />
              </ListItem>
            )}
          </List>
        </Box>
      </Drawer>

      <Toolbar />
      <Box flexGrow={1}>
        {noInnerBox ? children : <Container style={containerStyle}>{children}</Container>}
      </Box>
      {showFooter && (
      <Box
        component="footer"
        style={{
          backgroundColor: "gray",
          boxShadow: "5px 5px 20px #cfcfcf",
          textAlign: "center",
          padding: "5px",
          marginTop: "16px",
          zIndex: 1000,
          width: "100%",
        }}
      >
        <Typography variant="body2">&copy; 2020 SORA Corporation</Typography>
        <Button size="small" component={Link} to="/contact" style={{ marginLeft: "8px" }}>
          お問い合わせ
        </Button>
      </Box>
    )}
    </Box>
  );
};

export default Default;